/**
 * SearchBreadcrumbs - Visual wayfinder for rabbit hole navigation
 * 
 * Shows the user's search journey as clickable breadcrumbs.
 * Users can click any step to go back to that search.
 * 
 * Visual design:
 * - Compact horizontal trail
 * - Icons indicate search type
 * - Current step is highlighted
 * - Back/Forward arrows for quick navigation
 */

import React from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Home, 
  Building2, 
  Users, 
  Briefcase, 
  TrendingUp, 
  Zap,
  X,
  RotateCcw,
} from 'lucide-react';
import { useSearchPath } from '../../contexts/SearchPathContext';

// Get icon for source type
function getSourceIcon(sourceType) {
  switch (sourceType) {
    case 'company': return Building2;
    case 'person': return Users;
    case 'job': return Briefcase;
    case 'news': return TrendingUp;
    case 'skill': return Zap;
    case 'industry': return Building2;
    default: return Home;
  }
}

// Format step label
function formatStepLabel(step) {
  if (step.source?.name) {
    return step.source.name;
  }
  // Truncate query
  const query = step.query || 'Search';
  return query.length > 25 ? query.slice(0, 22) + '...' : query;
}

export default function SearchBreadcrumbs() {
  const { 
    path, 
    currentIndex, 
    goBack, 
    goForward, 
    goToStep, 
    clearPath,
    canGoBack, 
    canGoForward 
  } = useSearchPath();
  
  // Don't show if no path
  if (path.length === 0) {
    return null;
  }
  
  // Only show last 5 steps for compact view
  const visiblePath = path.slice(-5);
  const hiddenCount = Math.max(0, path.length - 5);
  
  return (
    <div className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-[var(--eq-purple)]/5 to-transparent rounded-xl border border-[var(--eq-purple)]/10">
      {/* Back button */}
      <button
        onClick={goBack}
        disabled={!canGoBack}
        className={`p-1.5 rounded-lg transition-all duration-150 ${
          canGoBack 
            ? 'hover:bg-[var(--eq-purple)]/10 text-[var(--eq-purple)] hover:scale-110 active:scale-95' 
            : 'text-black/20 cursor-not-allowed'
        }`}
        title="Go back"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      
      {/* Hidden steps indicator */}
      {hiddenCount > 0 && (
        <button
          onClick={() => goToStep(path[0].id)}
          className="flex items-center gap-1 px-2 py-1 text-xs text-black/40 hover:text-[var(--eq-purple)] hover:bg-[var(--eq-purple)]/5 rounded-lg transition-colors"
          title={`${hiddenCount} more steps`}
        >
          <RotateCcw className="w-3 h-3" />
          <span>+{hiddenCount}</span>
        </button>
      )}
      
      {/* Breadcrumb trail */}
      <div className="flex items-center gap-0.5 overflow-hidden">
        {visiblePath.map((step, idx) => {
          const actualIndex = path.indexOf(step);
          const isCurrent = actualIndex === currentIndex;
          const isPast = actualIndex < currentIndex;
          const Icon = getSourceIcon(step.source?.type);
          
          return (
            <React.Fragment key={step.id}>
              {/* Connector */}
              {idx > 0 && (
                <ChevronRight className="w-3 h-3 text-black/20 flex-shrink-0" />
              )}
              
              {/* Step button */}
              <button
                onClick={() => !isCurrent && goToStep(step.id)}
                disabled={isCurrent}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-all duration-150 max-w-[120px] ${
                  isCurrent
                    ? 'bg-[var(--eq-purple)] text-white shadow-sm'
                    : isPast
                      ? 'bg-white border border-black/10 text-black/60 hover:border-[var(--eq-purple)]/30 hover:text-[var(--eq-purple)] hover:scale-105 active:scale-95'
                      : 'bg-black/5 text-black/40 hover:bg-[var(--eq-purple)]/10 hover:text-[var(--eq-purple)] hover:scale-105 active:scale-95'
                }`}
                title={step.query}
              >
                <Icon className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{formatStepLabel(step)}</span>
                {step.results > 0 && (
                  <span className={`px-1 py-0.5 rounded text-[9px] ${
                    isCurrent ? 'bg-white/20' : 'bg-black/10'
                  }`}>
                    {step.results}
                  </span>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </div>
      
      {/* Forward button */}
      <button
        onClick={goForward}
        disabled={!canGoForward}
        className={`p-1.5 rounded-lg transition-all duration-150 ${
          canGoForward 
            ? 'hover:bg-[var(--eq-purple)]/10 text-[var(--eq-purple)] hover:scale-110 active:scale-95' 
            : 'text-black/20 cursor-not-allowed'
        }`}
        title="Go forward"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
      
      {/* Clear path button */}
      <button
        onClick={clearPath}
        className="p-1.5 ml-1 rounded-lg text-black/30 hover:text-red-500 hover:bg-red-50 transition-all duration-150 hover:scale-110 active:scale-95"
        title="Clear search history"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}


